import { FaqBlock, type FaqItem } from "@/components/FaqBlock";

export interface BrandModelLike {
  name: string;
  slug: string;
  year_min: number;
  year_max: number;
  vehicle_count: number;
}

/**
 * Data-derived editorial copy for a brand hub. Every sentence is generated
 * from that make's own catalogue (model count, coverage years, deepest
 * trim data), so no two brand pages read alike and the page carries real
 * text instead of a bare grid of links.
 */
export function buildBrandFaqs(makeName: string, models: BrandModelLike[]): FaqItem[] {
  if (models.length === 0) return [];
  const yearMin = Math.min(...models.map((m) => m.year_min));
  const yearMax = Math.max(...models.map((m) => m.year_max));
  const totalTrims = models.reduce((sum, m) => sum + m.vehicle_count, 0);
  const deepest = [...models].sort((a, b) => b.vehicle_count - a.vehicle_count)[0]!;
  const newest = [...models].sort((a, b) => b.year_max - a.year_max)[0]!;
  const oldest = [...models].sort((a, b) => a.year_min - b.year_min)[0]!;

  return [
    {
      q: `How many ${makeName} models are covered here?`,
      a: `${models.length} ${makeName} ${models.length === 1 ? "model line" : "model lines"} are covered, spanning ${totalTrims.toLocaleString()} individual trim and powertrain combinations from model years ${yearMin} through ${yearMax}. Each model page breaks those trims out year by year with EPA city, highway, and combined MPG, engine displacement, cylinder count, transmission, and drivetrain.`,
    },
    {
      q: `Which ${makeName} model has the most trim data?`,
      a: `The ${makeName} ${deepest.name} has the deepest coverage with ${deepest.vehicle_count.toLocaleString()} recorded ${deepest.vehicle_count === 1 ? "variant" : "variants"} between ${deepest.year_min} and ${deepest.year_max}. Model lines with more variants usually offered several engines, drivetrains, or transmission choices in the same year, so comparing trims within a single year matters more than comparing across years.`,
    },
    {
      q: `What model years of ${makeName} vehicles are included?`,
      a: `Coverage runs from ${yearMin} to ${yearMax}. The longest-running entry is the ${makeName} ${oldest.name} (from ${oldest.year_min}), and the most recently updated is the ${makeName} ${newest.name} (through ${newest.year_max}). Older years are included because used-car shoppers and owners looking up maintenance intervals need them as much as new-car buyers do.`,
    },
    {
      q: `Where does the ${makeName} data come from?`,
      a: `Fuel economy, engine, transmission, drivetrain, and vehicle-class fields come from the US EPA and Department of Energy fueleconomy.gov datasets, with vehicle identification cross-checked against the NHTSA vPIC database. Nothing is estimated by us: if a figure is missing for a given ${makeName} trim, the field is left blank rather than filled with a guess.`,
    },
    {
      q: `Are these ${makeName} MPG figures real-world numbers?`,
      a: `They are EPA laboratory test ratings, which is the same standard used on the window sticker. Real-world results for a ${makeName} typically land within about 10-15% of the combined rating, and drop further in cold weather, in stop-and-go traffic, at sustained highway speeds above 70 mph, or when towing.`,
    },
  ];
}

export function BrandOverview({
  makeName,
  models,
}: {
  makeName: string;
  models: BrandModelLike[];
}) {
  if (models.length === 0) return null;

  const yearMin = Math.min(...models.map((m) => m.year_min));
  const yearMax = Math.max(...models.map((m) => m.year_max));
  const totalTrims = models.reduce((sum, m) => sum + m.vehicle_count, 0);
  const sorted = [...models].sort((a, b) => b.vehicle_count - a.vehicle_count);
  const top = sorted.slice(0, 3);
  const current = models.filter((m) => m.year_max >= yearMax - 1);
  const legacy = models.filter((m) => m.year_max < yearMax - 1);
  const avgTrims = Math.round(totalTrims / models.length);
  const faqs = buildBrandFaqs(makeName, models);

  return (
    <section className="mx-auto max-w-6xl px-4 sm:px-6 pb-12">
      <div className="rounded-xl border border-border bg-card p-6 sm:p-8">
        <h2 className="font-heading text-2xl font-bold text-foreground">
          About {makeName} specifications on AutoSpecData
        </h2>

        <div className="mt-4 space-y-4 text-[15px] leading-relaxed text-muted-foreground">
          <p>
            This hub indexes every {makeName} model line we hold verified data for —{" "}
            {models.length} {models.length === 1 ? "nameplate" : "nameplates"} covering{" "}
            {totalTrims.toLocaleString()} distinct trim and powertrain combinations across
            model years {yearMin} to {yearMax}. That works out to roughly {avgTrims}{" "}
            {avgTrims === 1 ? "variant" : "variants"} per model line, which is why picking the
            exact year and engine matters when you compare fuel economy: two {makeName}
            {" "}vehicles wearing the same badge in the same year can differ by several MPG
            depending on drivetrain and transmission.
          </p>

          <p>
            The deepest {makeName} coverage sits with{" "}
            {top.map((m, i) => (
              <span key={m.slug}>
                {i > 0 ? (i === top.length - 1 ? " and " : ", ") : ""}
                the {m.name} ({m.vehicle_count.toLocaleString()}{" "}
                {m.vehicle_count === 1 ? "variant" : "variants"}, {m.year_min}–{m.year_max})
              </span>
            ))}
            . {current.length > 0 ? (
              <>
                {current.length} {makeName}{" "}
                {current.length === 1 ? "model is" : "models are"} still current as of{" "}
                {yearMax}
                {legacy.length > 0 ? (
                  <>
                    , while {legacy.length} {legacy.length === 1 ? "line has" : "lines have"}{" "}
                    been discontinued or renamed — those stay published because used buyers and
                    owners still look up their MPG, engine, and service data.
                  </>
                ) : (
                  "."
                )}
              </>
            ) : null}
          </p>

          <p>
            Every {makeName} model page below breaks its data into five focused views: fuel
            economy (EPA city, highway, and combined MPG plus estimated annual fuel cost),
            engine specifications (displacement, cylinder count, fuel type, and forced
            induction where applicable), transmission, drivetrain, and vehicle class. Where a{" "}
            {makeName} model is rated for towing or has published bed and cargo dimensions,
            those figures appear on the model page too. Figures come from the EPA and
            Department of Energy fueleconomy.gov datasets and are cross-checked against NHTSA
            vPIC records; blank fields mean the source data does not publish that value for
            that trim, not that the value is zero.
          </p>

          <p>
            If you are cross-shopping, start with the model you already have in mind, note the
            combined MPG for the specific year and drivetrain, then use the comparison tool to
            put it side by side against a rival. Annual fuel cost is usually the number that
            separates two otherwise similar {makeName} trims over a five-year ownership window.
          </p>
        </div>

        {faqs.length > 0 ? (
          <div className="mt-8 border-t border-border pt-6">
            <h2 className="font-heading text-xl font-bold text-foreground mb-4">
              {makeName} specs: common questions
            </h2>
            <FaqBlock items={faqs} />
          </div>
        ) : null}
      </div>
    </section>
  );
}